import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import useAppStore from '../../store/appStore';
import { toast } from 'react-toastify';
import {
  PageHeader,
  RefreshButton,
  toTitle,
  getStatusColor,
  formatDate,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';

const TOAST_IDS = {
  updated: 'billing-updated',
  cancelled: 'billing-cancelled',
};

const Billing = () => {
  const { t, locale } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'plans');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen]= useState(false);

  const {
    updateSubscription,

    billing,
    billingBootstrap,
    billingRefreshCore,
    billingFetchPayments,
    billingLoadMorePayments,
    billingCreateCheckoutSession,
    billingConfirmSubscription,
    billingDowngradeToFree,
  } = useAppStore();

  const {
    plans,
    plansLoading,
    plansError,

    currentSub,
    currentLoading,
    currentError,

    usage,
    usageLoading,
    usageError,

    compareData,
    compareLoading,
    compareError,

    payments,
    paymentsLoading,
    paymentsLoadingMore,
    paymentsError,
    totalPayments,
    hasMorePayments,

    redirectingPlan,
  } = billing;

  const setTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      setMobileMenuOpen(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'plans');
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      await billingBootstrap();
      await billingFetchPayments({ limit: 50 }, { append: false });
      try {
        updateSubscription?.(currentSub || null);
      } catch { }
    })();
  }, []);

  const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

  const moneyFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
  const displayMoney = (v) => {
    if (isNum(v)) return moneyFmt.format(v);
    if (v === 0) return moneyFmt.format(0);
    if (v === null || v === undefined || v === '') return '—';
    return String(v);
  };
  const displayAnyMoney = (v) => (isNum(v) ? displayMoney(v) : (v ?? '—'));
  const displayMinutes = (v) => {
    if (isNum(v)) return `${v}`;
    if (v === 0) return '0';
    if (v == null || v === '') return '—';
    return String(v);
  };
  const priceOf = (plan) => (isNum(plan?.monthly_price) ? plan.monthly_price : null);
  const hasCustomPricing = (plan) => priceOf(plan) === null;
  const displayMonthlyPrice = (plan) => {
    const mp = priceOf(plan);
    return mp === null ? t('customPricing') : `${displayMoney(mp)} / ${t('month')}`;
  };

  const isIncluded = (v) => {
    const s = String(v ?? '').trim().toLowerCase();
    if (v === false || v === 0 || s === '' || s === '0' || s === 'false' || s === 'no' || v == null) return false;
    return true;
  };

  const renderFeatures = (featuresObj) => {
    if (!featuresObj || typeof featuresObj !== 'object') return [];
    return Object.entries(featuresObj).map(([k, v]) => {
      const label = toTitle(k);
      const included = isIncluded(v);
      const detail =
        typeof v === 'string' && v.trim()
          ? `: ${v}`
          : isNum(v)
            ? `: ${v}`
            : '';
      return { key: k, text: `${label}${detail}`, included };
    });
  };

  const currentPlan = useMemo(() => {
    if (!plans?.length) return null;
    const nameFromUsage = toTitle(usage?.current_plan);
    const nameFromSub = toTitle(currentSub?.plan);
    const codeFromSub = String(currentSub?.plan_code || '').toLowerCase();
    return (
      plans.find((p) => toTitle(p.name) === nameFromUsage) ||
      plans.find((p) => toTitle(p.name) === nameFromSub) ||
      plans.find((p) => String(p.code || '').toLowerCase() === codeFromSub) ||
      null
    );
  }, [plans, usage, currentSub]);

  const currentPlanNameLower = (currentPlan?.name || '').toLowerCase();

  const hasActiveSubscription = useMemo(() => {
    const s = String(currentSub?.status || '').toLowerCase();
    return s === 'active' || s === 'trialing' || s === 'past_due';
  }, [currentSub]);

  const createCheckoutSession = async (planNameOrObj) => {
    const res = await billingCreateCheckoutSession(planNameOrObj);
    if (res.success && res.url) {
      window.location.assign(res.url);
    } else if (!res.success && res.error) {
      toast.error(res.error);
    }
  };

  const refreshAll = useCallback(async () => {
    await billingRefreshCore();
    await billingFetchPayments({ limit: 50 }, { append: false });
    try {
      updateSubscription?.(currentSub || null);
    } catch { }
  }, [billingRefreshCore, billingFetchPayments, updateSubscription, currentSub]);

  const handledCheckoutRef = useRef(false);
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    (async () => {
      if (handledCheckoutRef.current) return;
      handledCheckoutRef.current = true;
      if (checkout === 'success') {
        const sid = searchParams.get('session_id');
        if (sid) await billingConfirmSubscription(sid);
        await refreshAll();
        toast.dismiss(TOAST_IDS.updated);
        toast.success(t('subscriptionUpdated'), { toastId: TOAST_IDS.updated });
        setTab('current');
      }
      if (checkout === 'cancelled') {
        toast.info(t('checkoutCancelled'));
        setTab('plans');
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('checkout');
        next.delete('session_id');
        return next;
      });
    })();
  }, [searchParams, refreshAll]);

  const isCurrentFree = useMemo(() => {
    const raw = String(
      currentSub?.plan_code ??
      currentSub?.plan ??
      usage?.current_plan ??
      currentPlan?.code ??
      currentPlan?.name ??
      ''
    ).toLowerCase().trim();
    return ['free', 'free_tier', 'free tier', 'starter_free'].some((k) => raw.includes(k));
  }, [currentPlan, usage, currentSub]);

  const busy = !!redirectingPlan;

  const PlansSkeleton = () => (
    <div className="grid md:grid-cols-3 gap-6">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="controlled-glass rounded-2xl p-8 animate-pulse inner-highlight">
          <div className="h-6 w-32 bg-white/10 rounded mx-auto mb-8" />
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[...Array(4)].map((__, j) => (
              <div key={j} className="rounded-xl border border-white/5 p-4 bg-white/5">
                <div className="h-3 w-16 bg-white/5 rounded mb-2" />
                <div className="h-4 w-20 bg-white/10 rounded" />
              </div>
            ))}
          </div>
          <div className="space-y-4 mb-8">
            {[...Array(5)].map((__, k) => (
              <div key={k} className="flex items-center gap-3">
                <div className="h-5 w-5 bg-white/10 rounded-full" />
                <div className="h-3 w-40 bg-white/5 rounded" />
              </div>
            ))}
          </div>
          <div className="h-12 w-full bg-white/10 rounded-xl" />
        </div>
      ))}
    </div>
  );

  const CurrentSkeleton = () => (
    <div className="controlled-glass rounded-[32px] p-8 animate-pulse inner-highlight">
      <div className="flex items-center justify-between mb-10">
        <div className="h-8 w-48 bg-white/10 rounded-xl" />
        <div className="h-6 w-24 bg-white/10 rounded-full" />
      </div>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="h-6 w-40 bg-white/10 rounded-lg" />
          <div className="h-4 w-56 bg-white/5 rounded-md" />
          <div className="space-y-4 mt-8 pt-8 border-t border-white/5">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-4 w-32 bg-white/5 rounded" />
                <div className="h-4 w-24 bg-white/10 rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="h-6 w-40 bg-white/10 rounded-lg" />
          <div className="space-y-4">
            <div className="flex justify-between items-end mb-2">
              <div className="h-3 w-28 bg-white/5 rounded" />
              <div className="h-4 w-16 bg-white/10 rounded" />
            </div>
            <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-white/10 rounded-full w-2/3" />
            </div>
          </div>
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex justify-between items-center">
              <div className="h-4 w-28 bg-white/5 rounded" />
              <div className="h-4 w-16 bg-white/10 rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mt-12 pt-8 border-t border-white/5">
        <div className="h-12 w-32 bg-white/10 rounded-xl" />
        <div className="h-12 w-48 bg-white/5 rounded-xl border border-white/5" />
      </div>
    </div>
  );

  const HistorySkeleton = () => (
    <div className="controlled-glass rounded-2xl overflow-hidden animate-pulse inner-highlight">
      <div className="h-12 bg-white/5" />
      <div className="divide-y divide-white/5">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="grid grid-cols-6 gap-4 px-6 py-5">
            <div className="h-4 w-24 bg-white/10 rounded" />
            <div className="h-4 w-48 bg-white/10 rounded col-span-2" />
            <div className="h-4 w-16 bg-white/10 rounded" />
            <div className="h-6 w-16 bg-white/10 rounded-full" />
            <div className="h-4 w-20 bg-white/5 rounded justify-self-end" />
          </div>
        ))}
      </div>
    </div>
  );

  const isFreePlan = (p) => String(p?.name || p?.code || '').toLowerCase().includes('free');

  const PlanCard = ({ plan }) => {
    const isCurrent = (currentPlan?.name || '').toLowerCase() === (plan?.name || '').toLowerCase();
    const isEnterprise = (plan?.name || '').toLowerCase() === 'enterprise';
    const customPricing = hasCustomPricing(plan);
    const features = renderFeatures(plan?.features);
    const upgradingThisPlan = (redirectingPlan || '').toLowerCase() === (plan?.name || '').toLowerCase();
    const isFree = isFreePlan(plan);

    return (
      <div className={`relative controlled-glass p-8 rounded-[32px] inner-highlight group transition-all duration-300 flex flex-col ${isCurrent ? 'ring-2 ring-primary-500/50 shadow-2xl shadow-primary-500/10' : 'hover:border-white/20'
        }`}>
        {plan?.name?.toLowerCase() === 'pro' && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-primary-500 text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-primary-500/30">{t('mostPopular')}</span>
          </div>
        )}

        <div className="text-center mb-10">
          <h3 className="text-2xl font-bold text-white tracking-tight">{plan.name || 'Plan'}</h3>
          {isCurrent && <p className="text-[10px] font-bold text-primary-400 uppercase tracking-widest mt-2 px-3 py-1 bg-primary-500/10 rounded-full w-fit mx-auto border border-primary-500/20">{t('activeSubscription')}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-10">
          <div className="p-3.5 rounded-2xl bg-white/5 border border-white/5 flex flex-col items-center text-center">
            <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest mb-1">{t('monthly')}</span>
            <span className="text-sm font-bold text-white tracking-tight">{displayMonthlyPrice(plan)}</span>
          </div>
          <div className="p-3.5 rounded-2xl bg-white/5 border border-white/5 flex flex-col items-center text-center">
            <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest mb-1">{t('setup')}</span>
            <span className="text-sm font-bold text-white tracking-tight">{displayMoney(plan.setup_fee)}</span>
          </div>
          <div className="p-3.5 rounded-2xl bg-white/5 border border-white/5 flex flex-col items-center text-center">
            <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest mb-1">{t('included')}</span>
            <span className="text-sm font-bold text-white tracking-tight">{displayMinutes(plan.included_minutes)}</span>
          </div>
          <div className="p-3.5 rounded-2xl bg-white/5 border border-white/5 flex flex-col items-center text-center">
            <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest mb-1">{t('extra')}</span>
            <span className="text-sm font-bold text-white tracking-tight">
              {isNum(plan.extra_minute_rate) ? `${displayMoney(plan.extra_minute_rate)}/m` : displayMoney(plan.extra_minute_rate)}
            </span>
          </div>
        </div>

        <div className="space-y-4 mb-10 flex-1">
          {features.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              {f.included ? (
                <CheckCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-white/10 flex-shrink-0" />
              )}
              <span className={`text-sm font-medium ${f.included ? 'text-white/70' : 'text-white/20'}`}>
                {f.text}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-auto">
          {isCurrent ? (
            <button disabled className="w-full py-4 rounded-2xl bg-white/5 text-white/30 font-bold text-xs uppercase tracking-widest cursor-not-allowed border border-white/5">
              {t('currentPlan')}
            </button>
          ) : isEnterprise ? (
            <button
              onClick={() => setShowEnterpriseModal(true)}
              className="w-full btn btn-primary py-4 rounded-2xl font-bold text-xs uppercase tracking-widest shadow-lg shadow-primary-500/20"
            >
              {t('contactSales')}
            </button>
          ) : isFree ? (
            <button
              disabled
              className="w-full py-4 rounded-2xl bg-white/5 text-white/30 font-bold text-xs uppercase tracking-widest cursor-not-allowed border border-white/5"
            >
              {t('freeTier')}
            </button>
          ) : (
            <button
              onClick={() => createCheckoutSession(plan)}
              disabled={!!redirectingPlan}
              className={`w-full py-4 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all ${upgradingThisPlan
                ? 'bg-white/10 text-white/40 cursor-wait border border-white/10'
                : 'btn btn-primary shadow-lg shadow-primary-500/20'
                }`}
            >
              {upgradingThisPlan ? t('redirecting') : `${t('choose')} ${plan?.name}`}
            </button>
          )}
        </div>
      </div>
    );
  };

  const EnterpriseModal = () => {
    const [mounted, setMounted] = useState(false);
    const [copied, setCopied] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted || typeof window === 'undefined') return null;

    const copyEmail = () => {
      navigator.clipboard.writeText('hello@valoria.ai').then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowEnterpriseModal(false)}>
        <div className="controlled-glass rounded-[32px] p-10 max-w-md w-full mx-4 shadow-2xl inner-highlight overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setShowEnterpriseModal(false)} className="absolute top-5 right-5 p-2 text-white/20 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary-500/10 rounded-2xl flex items-center justify-center mb-8">
              <svg className="h-8 w-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-3">Contact Sales</h3>
            <p className="text-white/40 mb-8 font-medium">Get a custom quote for your team. Our sales team will get back to you within 24 hours.</p>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6 flex items-center justify-between gap-4">
              <span className="text-sm font-bold text-white tracking-tight">hello@valoria.ai</span>
              <button
                onClick={copyEmail}
                className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg bg-primary-500/10 border border-primary-500/20 text-primary-400 hover:bg-primary-500/20 transition-all flex-shrink-0"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <a
              href="mailto:hello@valoria.ai?subject=Enterprise Plan Inquiry"
              className="w-full btn btn-primary py-4 rounded-2xl font-bold text-xs uppercase tracking-widest shadow-lg shadow-primary-500/20 flex items-center justify-center mb-3"
            >
              Open Email Client
            </a>
            <button
              onClick={() => setShowEnterpriseModal(false)}
              className="w-full py-3 rounded-2xl bg-white/5 border border-white/10 text-white/40 font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const CancelModal = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted || typeof window === 'undefined') return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
        <div className="controlled-glass rounded-[32px] p-10 max-w-md w-full mx-4 shadow-2xl inner-highlight overflow-hidden relative">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-400/10 rounded-2xl flex items-center justify-center mb-8">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-3">{t('cancelSubscription')}</h3>
            <p className="text-white/40 mb-8 font-medium">
              {t('cancelSubDesc')} <span className="text-white">{t('freeTier')}</span>.
            </p>

            <div className="bg-white/5 border border-white/5 rounded-2xl p-4 mb-10">
              <p className="text-xs text-white/40 font-bold uppercase tracking-widest text-center">
                Subscription active until {formatDate(usage?.current_period_end)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setShowCancelModal(false)}
                className="btn py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all"
              >
                {t('keep')}
              </button>
              <button
                onClick={async () => {
                  const r = await billingDowngradeToFree();
                  if (r.success) {
                    setShowCancelModal(false);
                    toast.dismiss(TOAST_IDS.cancelled);
                    toast.success(r.already ? 'You are already on the Free plan.' : 'Downgraded to Free.', { toastId: TOAST_IDS.cancelled });
                    setTab('current');
                    await refreshAll();
                  } else {
                    toast.error(r.error || 'Failed to downgrade to Free');
                  }
                }}
                className="btn py-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-500/20"
              >
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const CompareModal = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted || typeof window === 'undefined') return null;

    const dataset = compareData?.plans?.length ? compareData.plans : (plans || {});
    const currency = compareData?.currency || 'USD';
    const billingCycle = compareData?.billing_cycle || 'monthly';
    const note = compareData?.note || '';

    const val = (x) => (x == null || x === '' ? null : Number.isFinite(+x) ? +x : null);
    const sortedPlans = [...(Array.isArray(dataset) ? dataset : [])].sort((a, b) => {
      const pa = val(a?.monthly_price);
      const pb = val(b?.monthly_price);
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pa - pb;
    });

    const headerCellClass = 'px-6 py-4 text-left font-bold text-[10px] uppercase tracking-widest text-white/40';
    const stickyFeatureClass = 'sticky left-0 controlled-glass px-6 py-4 font-bold text-white tracking-tight w-40 sm:w-64 text-sm z-10';

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
        <div className="controlled-glass rounded-[32px] shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden inner-highlight flex flex-col">
          <div className="flex items-center justify-between px-8 py-6 border-b border-white/5">
            <div>
              <h3 className="text-2xl font-bold text-white tracking-tight">{t('comparePlansTitle')}</h3>
              <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mt-1">
                {currency} • {billingCycle} cycle
              </p>
            </div>
            <button onClick={() => setShowCompareModal(false)} className="p-2 text-white/20 hover:text-white transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            {compareLoading && (
              <div className="p-10 space-y-8 animate-pulse">
                <div className="h-6 w-48 bg-white/10 rounded-lg" />
                <div className="h-96 w-full bg-white/5 rounded-2xl" />
              </div>
            )}
            {compareError && <div className="p-10 text-center"><p className="text-red-400 font-medium">{compareError}</p></div>}

            {!compareLoading && !compareError && (
              <div className="p-0">
                <table className="min-w-full border-separate border-spacing-0">
                  <thead>
                    <tr className="bg-white/5">
                      <th className={`${headerCellClass} sticky left-0 bg-[#1a1f35] w-40 sm:w-64 z-20`}>{t('feature')}</th>
                      {sortedPlans.map((p) => {
                        const isPopular = !!p.is_popular;
                        const isCurrent = currentPlanNameLower && String(p?.name || '').toLowerCase() === currentPlanNameLower;
                        return (
                          <th key={p.plan || p.code || p.id || p.name} className={`${headerCellClass}`}>
                            <div className="flex flex-col gap-2">
                              <span className="text-white text-sm">{p.name}</span>
                              <div className="flex flex-wrap gap-2">
                                {isPopular && (
                                  <span className="text-[8px] font-bold uppercase tracking-wide bg-amber-500/10 text-amber-500 border border-amber-500/20 px-1.5 py-0.5 rounded">
                                    {t('mostPopular')}
                                  </span>
                                )}
                                {isCurrent && (
                                  <span className="text-[8px] font-bold uppercase tracking-wide bg-primary-500/10 text-primary-400 border border-primary-500/20 px-1.5 py-0.5 rounded">
                                    {t('current')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    <tr>
                      <td className={stickyFeatureClass}>{t('monthlyPrice')}</td>
                      {sortedPlans.map((p) => (
                        <td key={(p.plan || p.name) + '_price'} className="px-6 py-4 text-sm font-medium text-white/70">
                          {typeof p.monthly_price === 'number' ? `${displayMoney(p.monthly_price)}/mo` : displayAnyMoney(p.monthly_price)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className={stickyFeatureClass}>{t('setupFee')}</td>
                      {sortedPlans.map((p) => (
                        <td key={(p.plan || p.name) + '_setup'} className="px-6 py-4 text-sm font-medium text-white/70">
                          {displayAnyMoney(p.setup_fee)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className={stickyFeatureClass}>{t('includedMinutes')}</td>
                      {sortedPlans.map((p) => (
                        <td key={(p.plan || p.name) + '_minutes'} className="px-6 py-4 text-sm font-medium text-white/70">
                          {displayMinutes(p.included_minutes)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className={stickyFeatureClass}>{t('extraMinuteRate')}</td>
                      {sortedPlans.map((p) => (
                        <td key={(p.plan || p.name) + '_extra'} className="px-6 py-4 text-sm font-medium text-white/70">
                          {typeof p.extra_minute_rate === 'number' ? `${displayMoney(p.extra_minute_rate)}/min` : displayAnyMoney(p.extra_minute_rate)}
                        </td>
                      ))}
                    </tr>
                    {Array.from(new Set(sortedPlans.flatMap((p) => Object.keys(p.features || {})))).map((k) => (
                      <tr key={k}>
                        <td className={stickyFeatureClass}>{toTitle(k)}</td>
                        {sortedPlans.map((p) => {
                          const v = p?.features?.[k];
                          if (v === undefined) return <td key={(p.plan || p.name) + k} className="px-6 py-4 text-white/10">—</td>;
                          const inc = isIncluded(v);
                          const text = typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : inc ? t('included') : t('no');
                          return (
                            <td key={(p.plan || p.name) + k} className="px-6 py-4 text-sm font-medium">
                              <div className="flex items-center gap-2">
                                {inc ? (
                                  <CheckCircleIcon className="h-4 w-4 text-primary-400" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-white/10" />
                                )}
                                <span className={inc ? 'text-white/70' : 'text-white/20'}>{text}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {note && (
            <div className="px-8 py-4 bg-white/5 border-t border-white/5">
              <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">{note}</p>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  const minutesLimit = isNum(usage?.minutes_limit) ? usage.minutes_limit : 0;
  const minutesUsed = isNum(usage?.minutes_used) ? usage.minutes_used : 0;
  const minutesPercent = minutesLimit > 0 ? Math.min(100, Math.round((minutesUsed / minutesLimit) * 100)) : 0;
  const minutesRemaining = minutesLimit > 0 ? Math.max(0, minutesLimit - minutesUsed) : null;

  const filteredPayments = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return payments;
    return payments.filter((item) => {
      const id = String(item?.invoice_id || item?.id || '').toLowerCase();
      const desc = String(item?.description || '').toLowerCase();
      const pm = String(item?.payment_method || '').toLowerCase();
      const status = String(item?.status || '').toLowerCase();
      const cur = String(item?.currency || '').toLowerCase();
      return id.includes(needle) || desc.includes(needle) || pm.includes(needle) || status.includes(needle) || cur.includes(needle);
    });
  }, [payments, searchTerm]);

  const downloadCsv = () => {
    const rows = [
      ['Created', 'Payment ID', 'Description', 'Amount', 'Currency', 'Status', 'Payment Method', 'Invoice URL', 'Receipt URL'],
      ...filteredPayments.map((it) => [
        new Date(it?.created || Date.now()).toLocaleString(),
        it?.id || '',
        it?.description || '',
        typeof it?.amount === 'number' ? it.amount : '',
        (it?.currency || 'EUR').toUpperCase(),
        it?.status || '',
        it?.payment_method || '',
        it?.invoice_url || '',
        it?.receipt_url || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openPaymentLinks = (p) => {
    const url = p?.invoice_url || p?.receipt_url || '';
    if (typeof url === 'string' && url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
    else toast.info('No downloadable link is available for this payment yet.');
  };

  const loadMorePayments = async () => {
    await billingLoadMorePayments();
  };

  const isAnyLoading = plansLoading || currentLoading || usageLoading || compareLoading || paymentsLoading;

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader title={t('billingAndSub')} subtitle={t('manageSub')}>
        <div className="flex justify-between sm:justify-start sm:space-x-2 w-full sm:w-auto">
          <RefreshButton onClick={refreshAll} isLoading={isAnyLoading} t={t} />
          {activeTab === 'history' && (
            <button
              onClick={downloadCsv}
              disabled={!filteredPayments.length}
              className={`btn ${!filteredPayments.length ? 'btn-disabled' : 'btn-secondary'}`}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('export')} CSV</span>
              <span className="sm:hidden">{t('export')}</span>
            </button>
          )}
        </div>
      </PageHeader>

      <div className="md:hidden">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full flex items-center justify-between p-4 controlled-glass rounded-xl inner-highlight"
        >
          <span className="font-bold text-white tracking-tight">
            {activeTab === 'plans' ? t('plansPricing') :
              activeTab === 'current' ? t('currentSub') : t('billingHistory')}
          </span>
          <Bars3Icon className="h-5 w-5 text-white/40" />
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden controlled-glass rounded-xl shadow-2xl mt-2 inner-highlight overflow-hidden">
          <nav className="flex flex-col p-2">
            <button
              onClick={() => { setTab('plans'); setMobileMenuOpen(false); }}
              className={`py-3 px-4 text-left rounded-lg font-bold text-xs uppercase tracking-widest ${activeTab === 'plans'
                ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                : 'text-white/40 hover:bg-white/5'
                }`}
            >
              {t('plansPricing')}
            </button>
            <button
              onClick={() => { setTab('current'); setMobileMenuOpen(false); }}
              className={`py-3 px-4 text-left rounded-lg font-bold text-xs uppercase tracking-widest ${activeTab === 'current'
                ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                : 'text-white/40 hover:bg-white/5'
                }`}
            >
              {t('currentSub')}
            </button>
            <button
              onClick={() => { setTab('history'); setMobileMenuOpen(false); }}
              className={`py-3 px-4 text-left rounded-lg font-bold text-xs uppercase tracking-widest ${activeTab === 'history'
                ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                : 'text-white/40 hover:bg-white/5'
                }`}
            >
              {t('billingHistory')}
            </button>
          </nav>
        </div>
      )}

      <div className="hidden md:block border-b border-white/5">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTab('plans')}
            className={`py-4 px-1 border-b-2 font-bold text-[10px] uppercase tracking-widest transition-all ${activeTab === 'plans'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
              }`}
          >
            {t('plansPricing')}
          </button>
          <button
            onClick={() => setTab('current')}
            className={`py-4 px-1 border-b-2 font-bold text-[10px] uppercase tracking-widest transition-all ${activeTab === 'current'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
              }`}
          >
            {t('currentSub')}
          </button>
          <button
            onClick={() => setTab('history')}
            className={`py-4 px-1 border-b-2 font-bold text-[10px] uppercase tracking-widest transition-all ${activeTab === 'history'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
              }`}
          >
            {t('billingHistory')}
          </button>
        </nav>
      </div>

      {activeTab === 'plans' && (
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-1">{t('choosePlan')}</h2>
              <p className="text-white/40 text-sm sm:text-base font-medium italic">{t('selectPlanDesc')}</p>
            </div>
            {!plansLoading && !plansError && (plans?.length > 0 || compareData?.plans?.length) && (
              <button
                onClick={() => setShowCompareModal(true)}
                className="btn py-2.5 px-6 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all w-full sm:w-auto"
              >
                {t('comparePlans')}
              </button>
            )}
          </div>

          {plansLoading && <PlansSkeleton />}
          {plansError && <p className="text-center text-red-600 text-sm sm:text-base">{plansError}</p>}

          {!plansLoading && !plansError && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {(plans ?? []).map((plan, idx) => (
                <PlanCard key={plan.id || plan.code || plan.name || idx} plan={plan} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'current' && (
        <div className="space-y-4 sm:space-y-6">
          {(currentLoading || usageLoading) && <CurrentSkeleton />}
          {(currentError || usageError) && !currentLoading && !usageLoading && <p className="text-red-600 text-sm sm:text-base">{currentError || usageError}</p>}

          {!currentLoading && !usageLoading && !currentError && !usageError && (
            <div className="controlled-glass rounded-[32px] p-8 inner-highlight shadow-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <h2 className="text-2xl font-bold text-white tracking-tight">{t('currentSub')}</h2>
                <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${getStatusColor(currentSub?.status)} self-start sm:self-auto`}>
                  {currentSub?.status ? t(currentSub.status.toLowerCase()) : '—'}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-white tracking-tight mb-2">
                      {currentPlan?.name || toTitle(usage?.current_plan) || 'Plan'}
                    </h3>
                    <p className="text-sm font-medium text-white/40 italic">
                      {currentPlan
                        ? isNum(currentPlan.included_minutes)
                          ? `${currentPlan.included_minutes} ${t('includedMinutes').toLowerCase()}`
                          : String(currentPlan.included_minutes || '')
                        : isNum(usage?.minutes_limit)
                          ? `${usage.minutes_limit} ${t('includedMinutes').toLowerCase()}`
                          : ''}
                    </p>
                  </div>

                  <div className="space-y-3 pt-6 border-t border-white/5">
                    {priceOf(currentPlan) !== null && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-white/30 uppercase tracking-widest">{t('monthlyCost')}</span>
                        <span className="text-sm font-bold text-white">{displayMonthlyPrice(currentPlan)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-white/30 uppercase tracking-widest">{t('autoRenewal')}</span>
                      <span className={`text-sm font-bold ${currentSub?.cancel_at_period_end ? 'text-red-400' : 'text-primary-400'}`}>
                        {currentSub?.cancel_at_period_end ? t('disabled') : t('enabled')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-xs font-bold text-white/30 uppercase tracking-widest">{t('usageThisMonth')}</h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                      <div className="flex justify-between mb-3">
                        <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('minutesUsed')}</span>
                        <span className="text-xs font-bold text-white">
                          {isNum(usage?.minutes_used) && isNum(usage?.minutes_limit) ? `${usage.minutes_used} / ${usage.minutes_limit}` : '—'}
                        </span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                        <div className="bg-primary-500 h-full rounded-full transition-all duration-500 shadow-[0_0_12px_rgba(var(--primary-500-rgb),0.3)]" style={{ width: `${minutesPercent}%` }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                        <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest block mb-1">{t('remaining')}</span>
                        <span className="text-sm font-bold text-white tracking-tight">{minutesRemaining != null ? minutesRemaining : '—'}</span>
                      </div>
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                        <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest block mb-1">{t('overage')}</span>
                        <span className="text-sm font-bold text-white tracking-tight">{isNum(usage?.overage_cost) ? displayMoney(usage.overage_cost) : '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-10 pt-10 border-t border-white/5">
                <button
                  onClick={() => setTab('plans')}
                  disabled={busy}
                  className={`btn min-w-[160px] py-4 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${busy ? 'bg-white/10 text-white/20 cursor-not-allowed' : 'btn-primary shadow-lg shadow-primary-500/20'
                    }`}
                >
                  {hasActiveSubscription ? t('changePlan') : t('choosePlan')}
                </button>

                {hasActiveSubscription && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="btn py-4 px-8 rounded-xl bg-white/5 border border-white/10 text-white/40 font-bold text-xs uppercase tracking-widest hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition-all"
                  >
                    {t('cancelSubscription')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {t('billingHistoryTitle')} {totalPayments ? <span className="text-white/40 text-sm sm:text-base font-medium italic">• {totalPayments} {t('payments')}</span> : ''}
            </h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <div className="relative group flex-1 min-w-[300px]">
                <MagnifyingGlassIcon className="h-4 w-4 text-white/20 absolute left-4 top-1/2 transform -translate-y-1/2 group-focus-within:text-white transition-colors" />
                <input
                  type="text"
                  placeholder={t('searchPayments')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-11"
                />
              </div>
            </div>
          </div>

          {paymentsLoading && !payments.length && <HistorySkeleton />}
          {paymentsError && <p className="text-red-600 text-sm sm:text-base">{paymentsError}</p>}

          {!paymentsLoading && !paymentsError && filteredPayments.length === 0 && (
            <div className="text-center py-8 sm:py-16 px-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto mb-4 sm:mb-6 h-10 sm:h-14 w-10 sm:w-14 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a9 9 0 00-9 9v2a9 9 0 009 9 9 9 0 009-9v-2a9 9 0 00-9-9z" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 15c1.333 1 2.667 1 4 0" />
              </svg>
              <h3 className="mt-2 text-base sm:text-lg font-semibold text-white">{t('noPaymentsFound')}</h3>
              <p className="mt-1 text-xs sm:text-sm text-white/40 max-w-md mx-auto">
                {t('billingHistoryEmptyDesc')}
              </p>
            </div>
          )}

          {!paymentsError && filteredPayments.length > 0 && (
            <div className="controlled-glass rounded-[32px] overflow-hidden inner-highlight shadow-2xl">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/5">
                  <thead className="bg-[#1a1f35]">
                    <tr>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-nowrap">
                        {t('created')}
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-nowrap">
                        {t('invoiceId')}
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-normal min-w-[200px]">
                        {t('description')}
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-nowrap">
                        {t('amount')}
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-nowrap">
                        {t('status')}
                      </th>
                      <th className="px-6 py-4 text-left text-[10px] font-bold text-white/30 uppercase tracking-widest whitespace-nowrap">
                        {t('actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 bg-[#1a1f35]/50">
                    {filteredPayments.map((it) => {
                      const created = it?.created ? new Date(it.created).toLocaleString(locale) : '—';
                      const id = it?.id || '—';
                      const invoiceId = it?.invoice_id || '';
                      const desc = it?.description || '—';
                      const amountFormatted =
                        it?.amount_formatted ||
                        (typeof it?.amount === 'number'
                          ? new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency: (it?.currency || 'USD').toUpperCase(),
                            maximumFractionDigits: 2,
                          }).format(it.amount)
                          : '—');
                      const status = it?.status || '—';
                      return (
                        <tr key={`${id}-${invoiceId}`} className="hover:bg-white/5 transition-colors group">
                          <td className="px-6 py-5 whitespace-nowrap text-xs font-medium text-white/70">{created}</td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold text-white tracking-tight break-all">{id}</span>
                              {invoiceId ? <span className="text-[10px] font-bold text-white/20 uppercase tracking-widest break-all">Inv: {invoiceId}</span> : null}
                            </div>
                          </td>
                          <td className="px-6 py-5 text-sm font-medium text-white/40 italic whitespace-normal break-words max-w-[300px]">
                            {desc}
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap text-xs font-bold text-white tracking-tight">{amountFormatted}</td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <span
                              className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${getStatusColor(status)}`}
                            >
                              {status ? t(status.toLowerCase()) : '—'}
                            </span>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <button
                              onClick={() => openPaymentLinks(it)}
                              className="text-primary-400 hover:text-primary-300 font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-lg bg-primary-500/5 border border-primary-500/10 hover:bg-primary-500/10 transition-all"
                            >
                              {t('download')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-white/5 bg-[#1a1f35] gap-4">
                <div className="text-[10px] font-bold text-white/30 uppercase tracking-widest text-center sm:text-left">
                  {filteredPayments.length} {t('ofSuffix')} {totalPayments ?? payments.length} {t('shown')}
                </div>
                {hasMorePayments ? (
                  <button
                    onClick={loadMorePayments}
                    disabled={paymentsLoadingMore}
                    className={`btn py-2 px-6 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${paymentsLoadingMore ? 'bg-white/5 text-white/20 cursor-wait border border-white/5' : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                      }`}
                  >
                    {paymentsLoadingMore ? t('loading') : t('loadMore')}
                  </button>
                ) : (
                  <span className="text-[10px] font-bold text-white/10 uppercase tracking-widest text-center sm:text-right">{t('noMorePayments')}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showEnterpriseModal && <EnterpriseModal />}
      {showCancelModal && <CancelModal />}
      {showCompareModal && <CompareModal />}
    </div>
  );
};

export default Billing;